import { useEffect, useMemo, useState } from "react";
import type { Quote } from "@roskomkassa/frontend-shared";
import { post } from "./api";
import { requestedCheckoutPlan } from "./checkout-intent";
import type { Plan } from "./types";

export function useSubscriptionCheckout(plans: Plan[], preferredDevices: number, initialPromo: string) {
  const fallback = plans.find((plan) => plan.max_devices === preferredDevices) ?? plans[0];
  const [selectedCode, setSelectedCode] = useState<string | undefined>(() => requestedCheckoutPlan(window.location.search, plans)?.code ?? fallback?.code);
  const selected = plans.find((plan) => plan.code === selectedCode) ?? fallback;
  const selectedDevices = selected?.max_devices;
  const availablePlans = useMemo(() => plans.filter((plan) => plan.max_devices === selectedDevices), [plans, selectedDevices]);
  const deviceOptions = useMemo(() => [...new Set(plans.map((plan) => plan.max_devices))].sort((a, b) => a - b), [plans]);
  const [promo, setPromo] = useState(initialPromo);
  const [revision, setRevision] = useState(0);
  const quoteKey = JSON.stringify([selected?.code, selected?.price_minor, selected?.currency, promo, revision]);
  const [result, setResult] = useState<{ key: string; quote: Quote | null; error: string } | null>(null);

  useEffect(() => {
    const onNavigation = () => {
      setResult(null);
      setRevision((value) => value + 1);
      setSelectedCode(requestedCheckoutPlan(window.location.search, plans)?.code ?? fallback?.code);
    };
    window.addEventListener("popstate", onNavigation);
    return () => window.removeEventListener("popstate", onNavigation);
  }, [plans, fallback?.code]);

  useEffect(() => {
    if (!selected) return;
    let cancelled = false;
    void post<Quote>("/app/subscription/quote", { plan_code: selected.code, promo_code: promo || null })
      .then((quote) => {
        if (quote.plan.code !== selected.code || quote.currency !== selected.currency || !Number.isSafeInteger(quote.total_price_minor) || quote.total_price_minor < 0) {
          throw new Error("Не удалось подтвердить цену выбранного тарифа. Повторите расчёт.");
        }
        if (!cancelled) setResult({ key: quoteKey, quote, error: "" });
      })
      .catch((error: unknown) => {
        if (!cancelled) setResult({ key: quoteKey, quote: null, error: error instanceof Error ? error.message : "Не удалось рассчитать стоимость. Повторите расчёт." });
      });
    return () => { cancelled = true; };
  }, [selected, promo, quoteKey]);

  const current = result?.key === quoteKey ? result : null;
  return {
    selected, selectedDevices, availablePlans, deviceOptions, promo,
    setPromo: (value: string) => { if (value !== promo) { setResult(null); setPromo(value); } },
    quoteData: current?.quote ?? null,
    quoteError: current?.error ?? "",
    quotePending: Boolean(selected) && !current,
    retryQuote: () => setRevision((value) => value + 1),
    selectPlan: (plan: Plan) => { if (plan.code !== selected?.code) { setResult(null); setSelectedCode(plan.code); } },
    selectDevices: (count: number) => {
      if (count === selectedDevices) return;
      setResult(null);
      setSelectedCode((plans.find((plan) => plan.max_devices === count && plan.duration_days === selected?.duration_days) ?? plans.find((plan) => plan.max_devices === count))?.code);
    },
  };
}
